<template>
  <div class="org-logo-field org-logo-field--dark">
    <div
      class="org-logo-dropzone"
      :class="{
        'org-logo-dropzone--filled': previewUrl,
        'org-logo-dropzone--uploading': uploading,
        'org-logo-dropzone--error': error,
      }"
      role="button"
      tabindex="0"
      @click="triggerPicker"
      @keydown.enter.prevent="triggerPicker"
      @keydown.space.prevent="triggerPicker"
    >
      <input
        ref="fileInput"
        type="file"
        class="org-logo-dropzone__input"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
        :disabled="disabled || uploading"
        @change="onInputChange"
      />

      <div
        v-if="uploading"
        class="org-logo-dropzone__overlay d-flex flex-column align-center justify-center"
      >
        <v-progress-circular indeterminate color="primary" size="24" width="2" />
      </div>

      <template v-else-if="previewUrl">
        <img :src="previewUrl" alt="Organization logo" class="org-logo-dropzone__img" />
        <v-btn
          icon
          size="x-small"
          variant="flat"
          color="surface"
          class="org-logo-dropzone__remove"
          aria-label="Remove logo"
          :disabled="disabled || uploading"
          @click.stop="clear"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </template>

      <template v-else-if="loadingPreview">
        <v-progress-circular indeterminate color="primary" size="24" width="2" />
      </template>

      <template v-else>
        <v-icon color="primary" size="24">mdi-image-plus</v-icon>
      </template>
    </div>
    <p v-if="error" class="text-caption text-error mt-1 mb-0">{{ error }}</p>
  </div>
</template>

<script>
import UploadOrgLogo, { getOrgLogoExtension } from '@/mixins/Organization/UploadOrgLogo.js'
import {
  loadOrgLogoPreviewUrl,
  revokeOrgLogoPreviewUrl,
} from '@/utils/orgLogoStorage'
import { isNoLogoSelected, YANDLE_MARK_PUBLIC_PATH } from '@/utils/orgLogo'

export default {
  name: 'OrganizationLogoUpload',
  mixins: [UploadOrgLogo],
  props: {
    modelValue: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "error", "uploading"],
  data() {
    return {
      previewUrl: null,
      previewIsBlob: false,
      uploading: false,
      loadingPreview: false,
      error: "",
      localBlobActive: false,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(key) {
        if (this.localBlobActive) return;
        this.loadPreviewFromKey(key);
      },
    },
    uploading(val) {
      this.$emit("uploading", val);
    },
  },
  beforeUnmount() {
    this.revokePreview();
  },
  methods: {
    revokePreview() {
      revokeOrgLogoPreviewUrl(this.previewUrl, this.previewIsBlob)
      this.previewUrl = null
      this.previewIsBlob = false
    },
    async loadPreviewFromKey(key) {
      this.revokePreview()
      this.error = ''
      const path = String(key || '').trim()
      if (!path) return

      if (isNoLogoSelected(path)) {
        this.previewUrl = YANDLE_MARK_PUBLIC_PATH
        this.previewIsBlob = false
        return
      }

      if (/^https?:\/\//i.test(path) || /^data:/i.test(path)) {
        this.previewUrl = path
        this.previewIsBlob = false
        return
      }

      this.loadingPreview = true
      try {
        const { url, isBlob } = await loadOrgLogoPreviewUrl(path)
        if (url && path === String(this.modelValue || '').trim()) {
          this.previewUrl = url
          this.previewIsBlob = isBlob
        }
      } catch (err) {
        console.log('Org logo preview failed', err)
      } finally {
        this.loadingPreview = false
      }
    },
    triggerPicker() {
      if (this.disabled || this.uploading || this.loadingPreview) return;
      this.$refs.fileInput?.click();
    },
    validateFile(file) {
      if (!file) return null;
      if (!getOrgLogoExtension(file)) {
        return "Only PNG, JPG, WEBP, or SVG images are allowed";
      }
      if ((file.size ?? 0) > 2 * 1024 * 1024) {
        return "Logo must be 2 MB or smaller";
      }
      return null;
    },
    async onInputChange(event) {
      const file = event.target?.files?.[0] || null;
      event.target.value = "";
      if (!file) return;

      const validationError = this.validateFile(file);
      if (validationError) {
        this.error = validationError;
        return;
      }

      this.error = "";
      this.localBlobActive = true;
      this.revokePreview();
      this.previewUrl = URL.createObjectURL(file);
      this.previewIsBlob = true;

      this.uploading = true;
      try {
        const result = await this.uploadOrgLogo(file);
        if (result?.status === "SUCCESS") {
          this.localBlobActive = false;
          this.$emit("update:modelValue", result.s3_key);
        } else {
          this.error = result?.message || "Failed to upload logo";
          this.revokePreview();
          this.localBlobActive = false;
          this.$emit("error", result);
        }
      } catch (err) {
        this.error = "Failed to upload logo";
        this.revokePreview();
        this.localBlobActive = false;
        console.log("Err", err);
      } finally {
        this.uploading = false;
      }
    },
    clear() {
      this.revokePreview();
      this.localBlobActive = false;
      this.error = "";
      this.uploading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      this.$emit("update:modelValue", "");
    },
  },
};
</script>

<style scoped>
.org-logo-dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  padding: var(--y-space-2, 8px);
  border: 1px dashed var(--y-border-strong, rgba(0, 0, 0, 0.2));
  border-radius: 50%;
  background: var(--y-surface, #fff);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  overflow: hidden;
}

.org-logo-dropzone:hover:not(.org-logo-dropzone--uploading) {
  border-color: rgb(var(--v-theme-primary));
  background: var(--y-primary-muted, rgba(var(--v-theme-primary), 0.06));
}

.org-logo-dropzone--filled {
  padding: 0;
  border-style: solid;
}

.org-logo-dropzone--uploading {
  cursor: wait;
}

.org-logo-dropzone--error {
  border-color: rgb(var(--v-theme-error));
}

.org-logo-dropzone__input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.org-logo-dropzone__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--y-surface, #fff);
}

.org-logo-dropzone__overlay {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  z-index: 1;
}

.org-logo-dropzone__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
}

.org-logo-field--dark .org-logo-dropzone {
  border-color: rgba(255, 255, 255, 0.2);
  background: var(--y-glass-surface);
}

.org-logo-field--dark .org-logo-dropzone:hover:not(.org-logo-dropzone--uploading) {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.12);
}

.org-logo-field--dark .org-logo-dropzone__overlay {
  background: rgba(15, 23, 42, 0.75);
}

.org-logo-field--dark .text-error {
  color: #fca5a5 !important;
}
</style>
